"use client"

// Produit.js

import { useState, useEffect } from "react"
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  CircularProgress,
  Typography,
  List,
  ListItem,
  ListItemText,
  Paper,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Chip,
  Tooltip,
  Zoom,
  Stack,
  alpha,
} from "@mui/material"
import { DataGrid, GridToolbar } from "@mui/x-data-grid"
import {
  Delete,
  Edit,
  Search,
  ShoppingCart,
  AddCircleOutline,
  Inventory2Outlined,
  ArrowDropDown,
} from "@mui/icons-material"
import { useSelector, useDispatch } from "react-redux"
import {
  fetchProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  createAchatProduct,
  fetchAchatProducts,
  updateAchatProduct,
  deleteAchatProduct,
} from "../../redux/actions/product"
import { selectShop } from "../../redux/actions/shop"
import { toast } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import moment from "moment"
import { tokens } from "../../theme"

const Produit = () => {
  const dispatch = useDispatch()
  const { user } = useSelector((state) => state.user)
  const { products, achatProducts, loading, error } = useSelector((state) => state.product)
  const { shops, selectedShop } = useSelector((state) => state.shop)
  const theme = useTheme()
  const colors = tokens(theme.palette.mode)

  const [openAdd, setOpenAdd] = useState(false)
  const [openEdit, setOpenEdit] = useState(false)
  const [openAchat, setOpenAchat] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        if (!user) return

        if (user.role === "superadmin" || user.role === "supervisor") {
          if (shops.length > 0) {
            const firstShop = electronicShops[0]
            if (firstShop) {
              dispatch(selectShop(firstShop))
              await dispatch(fetchProducts(firstShop._id))
            }
          }
        } else if (user.shop) {
          const shopId = typeof user.shop === "object" ? user.shop._id : user.shop
          if (shopId) {
            await dispatch(fetchProducts(shopId))
          }
        }
      } catch (error) {
        console.error("Error loading credit sales:", error)
        toast.error("Failed to load credit sales")
      }
    }

    loadInitialData()
  }, [dispatch, user, shops.length])

  useEffect(() => {
    if (error) {
      toast.error(error)
    }
  }, [error])

  useEffect(() => {
    if (selectedProduct) {
      dispatch(fetchAchatProducts(selectedProduct._id))
    }
  }, [selectedProduct, dispatch])

  const filteredProducts = products.filter(
    (product) =>
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.category.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  const columns = [
    {
      field: "createdAt",
      headerName: "Date",
      flex: 1,
      valueGetter: (params) => moment(params.row?.date).format("DD-MM-YYYY"),
      renderCell: (params) => (
        <Tooltip title={moment(params.row?.date).format("DD-MM-YYYY HH:mm:ss")} arrow>
          <span>{moment(params.row?.date).format("DD-MM-YYYY")}</span>
        </Tooltip>
      ),
    },
    {
      field: "name",
      headerName: "Nom du produit",
      flex: 1.2,
      renderCell: (params) => (
        <Tooltip title={params.value} arrow>
          <span style={{ fontWeight: "bold" }}>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "category",
      headerName: "Categorie",
      flex: 1,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          variant="outlined"
          sx={{ borderColor: "grey.500", color: "grey.700" }}
        />
      ),
    },
    {
      field: "price_achat",
      headerName: "Prix d'achat",
      type: "number",
      flex: 0.8,
      renderCell: (params) => (
        <Typography fontWeight="bold" color="text.secondary">
          {(params.value ?? 0).toLocaleString()} F
        </Typography>
      ),
    },
    {
      field: "prix_de_vente",
      headerName: "Prix de vente",
      type: "number",
      flex: 0.8,
      renderCell: (params) => (
        <Typography fontWeight="bold" color="text.secondary">
          {(params.value ?? 0).toLocaleString()} F
        </Typography>
      ),
    },
    {
      field: "stock",
      headerName: "Quantité Stock",
      type: "number",
      flex: 0.8,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          variant={params.value > 10 ? "filled" : params.value > 0 ? "outlined" : "filled"}
          color={params.value > 10 ? "success" : params.value > 0 ? "warning" : "error"}
          sx={{ fontWeight: "bold" }}
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <Box>
          {user.role !== "supervisor" && (
            <Tooltip title="Acheter" arrow>
              <IconButton onClick={() => handleAchatOpen(params.row)} size="small">
                <ShoppingCart />
              </IconButton>
            </Tooltip>
          )}
          {user.role === "superadmin" && (
            <>
              <Tooltip title="Modifier" arrow>
                <IconButton onClick={() => handleEditClick(params.row)} size="small">
                  <Edit />
                </IconButton>
              </Tooltip>
              <Tooltip title="Supprimer" arrow>
                <IconButton onClick={() => handleDelete(params.row._id)} size="small">
                  <Delete />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      ),
    },
  ]

  const handleAddOpen = () => setOpenAdd(true)
  const handleAddClose = () => setOpenAdd(false)

  const handleEditOpen = () => setOpenEdit(true)
  const handleEditClose = () => setOpenEdit(false)

  const handleAchatOpen = (product) => {
    setSelectedProduct(product)
    setOpenAchat(true)
  }

  const handleAchatClose = () => {
    setOpenAchat(false)
    setSelectedProduct(null)
  }

  const handleAddProduct = async (productData) => {
    let shopId = null

    if (user.role === "superadmin" || user.role === "supervisor") {
      if (!selectedShop) {
        toast.error("Please select a shop to add a product.")
        return
      }
      shopId = selectedShop._id
    } else {
      shopId = user.shop._id
    }

    setIsSubmitting(true)
    const productToSubmit = {
      ...productData,
      price_achat: Number(productData.price_achat),
      stock: Number(productData.stock),
      shop: shopId,
    }

    try {
      await dispatch(createProduct(productToSubmit))
      handleAddClose()
      toast.success("Product added successfully!")
      if (user.role === "superadmin" || user.role === "supervisor") {
        dispatch(fetchProducts(selectedShop._id))
      } else {
        dispatch(fetchProducts(user.shop._id))
      }
    } catch (error) {
      toast.error(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleEditClick = (product) => {
    setSelectedProduct(product)
    handleEditOpen()
  }

  const handleEditProduct = async (productData) => {
    setIsSubmitting(true)
    const productToSubmit = {
      ...productData,
      price_achat: Number(productData.price_achat),
      stock: Number(productData.stock),
    }

    try {
      await dispatch(updateProduct(selectedProduct._id, productToSubmit))
      handleEditClose()
      toast.success("Product updated successfully!")
      if (user.role === "superadmin" || user.role === "supervisor") {
        dispatch(fetchProducts(selectedShop._id))
      } else {
        dispatch(fetchProducts(user.shop._id))
      }
    } catch (error) {
      toast.error(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      try {
        await dispatch(deleteProduct(id))
        toast.success("Product deleted successfully!")
        if (user.role === "superadmin" || user.role === "supervisor") {
          dispatch(fetchProducts(selectedShop._id))
        } else {
          dispatch(fetchProducts(user.shop._id))
        }
      } catch (error) {
        toast.error(error)
      }
    }
  }

  const handleShopChange = (event) => {
    const shopId = event.target.value
    if (shopId === "all") {
      dispatch(selectShop(null))
    } else {
      const shop = shops.find((s) => s._id === shopId)
      dispatch(selectShop(shop))
    }
    dispatch(fetchProducts(shopId === "all" ? null : shopId))
  }

  const handleAchatSubmit = async (achatData) => {
    if (!achatData.quantity) {
      toast.error("Quantity is required.")
      return
    }
    setIsSubmitting(true)
    try {
      await dispatch(createAchatProduct(selectedProduct._id, Number(achatData.quantity), achatData.description))
      toast.success("Achat product added successfully!")
      dispatch(fetchAchatProducts(selectedProduct._id))
      if (user.role === "superadmin" || user.role === "supervisor") {
        dispatch(fetchProducts(selectedShop._id))
      } else {
        dispatch(fetchProducts(user.shop._id))
      }
    } catch (error) {
      toast.error(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleAchatEdit = async (achatId) => {
    const newQuantity = prompt("Veuillez entrer la nouvelle quantité:")
    const newDescription = prompt("Veuillez entrer la description:")
    if (newQuantity !== null && newDescription !== null) {
      try {
        await dispatch(updateAchatProduct(achatId, Number(newQuantity), newDescription))
        toast.success("Achat product updated successfully!")
        dispatch(fetchAchatProducts(selectedProduct._id))
        if (user.role === "superadmin" || user.role === "supervisor") {
          dispatch(fetchProducts(selectedShop._id))
        } else {
          dispatch(fetchProducts(user.shop._id))
        }
      } catch (error) {
        toast.error(error)
      }
    }
  }

  const handleAchatDelete = async (achatId) => {
    if (window.confirm("Are you sure you want to delete this achat?")) {
      try {
        await dispatch(deleteAchatProduct(achatId))
        toast.success("Achat product deleted successfully!")
        dispatch(fetchAchatProducts(selectedProduct._id))
        if (user.role === "superadmin" || user.role === "supervisor") {
          dispatch(fetchProducts(selectedShop._id))
        } else {
          dispatch(fetchProducts(user.shop._id))
        }
      } catch (error) {
        toast.error(error)
      }
    }
  }

  const ProductForm = ({ initialProduct, isEdit, onSubmit, onCancel }) => {
    const [product, setProduct] = useState(initialProduct)

    const handleInputChange = (e) => {
      const { name, value } = e.target
      setProduct((prev) => ({ ...prev, [name]: value }))
    }

    const handleFormSubmit = () => {
      if (!product.name || !product.category) {
        toast.error("Name and Category are required.")
        return
      }
      onSubmit(product)
    }

    return (
      <DialogContent>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              margin="dense"
              name="name"
              label="Name"
              type="text"
              fullWidth
              variant="outlined"
              value={product.name}
              onChange={handleInputChange}
              required
              sx={{ mb: 2 }}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth margin="dense" required sx={{ mb: 2 }}>
              <InputLabel>Category</InputLabel>
              <Select name="category" value={product.category} onChange={handleInputChange} label="Category">
                {["BIKIGITI Grand Format", "BIKIGITI Petit Format"].map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              margin="dense"
              name="price_achat"
              label="Price Achat"
              type="number"
              fullWidth
              variant="outlined"
              value={product.price_achat}
              onChange={handleInputChange}
              InputProps={{
                startAdornment: <InputAdornment position="start">F</InputAdornment>,
              }}
              sx={{ mb: 2 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              margin="dense"
              name="prix_de_vente"
              label="Price de vente"
              type="number"
              fullWidth
              variant="outlined"
              value={product.prix_de_vente}
              onChange={handleInputChange}
              InputProps={{
                startAdornment: <InputAdornment position="start">F</InputAdornment>,
              }}
              sx={{ mb: 2 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              margin="dense"
              name="stock"
              label="Stock"
              type="number"
              fullWidth
              variant="outlined"
              value={product.stock}
              onChange={handleInputChange}
              sx={{ mb: 2 }}
            />
          </Grid>
        </Grid>
        <DialogActions sx={{ mt: 3, px: 0 }}>
          <Button onClick={onCancel} variant="outlined" color="primary">
            Annuler
          </Button>
          <Button onClick={handleFormSubmit} variant="contained" color="primary" disabled={isSubmitting}>
            {isSubmitting ? <CircularProgress size={24} /> : isEdit ? "Enregister" : "Ajouter"}
          </Button>
        </DialogActions>
      </DialogContent>
    )
  }

  const AchatForm = ({ onSubmit }) => {
    const [achatData, setAchatData] = useState({ quantity: "", description: "" })

    const handleInputChange = (e) => {
      const { name, value } = e.target
      setAchatData((prev) => ({ ...prev, [name]: value }))
    }

    const handleFormSubmit = () => {
      onSubmit(achatData)
    }

    return (
      <DialogContent>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              margin="dense"
              name="quantity"
              label="Quantity"
              type="number"
              fullWidth
              variant="outlined"
              value={achatData.quantity}
              onChange={handleInputChange}
              required
              sx={{ mb: 2 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              margin="dense"
              name="description"
              label="Description"
              type="text"
              fullWidth
              variant="outlined"
              value={achatData.description}
              onChange={handleInputChange}
              multiline
              rows={2}
              sx={{ mb: 2 }}
            />
          </Grid>
        </Grid>
        <Box mt={3}>
          <Button onClick={handleFormSubmit} variant="contained" color="primary" disabled={isSubmitting} fullWidth>
            {isSubmitting ? <CircularProgress size={24} /> : "Ajouter l'Achat"}
          </Button>
        </Box>
      </DialogContent>
    )
  }

  const electronicShops = shops.filter((shop) => shop.enum === "electronics" || shop.type === "electronics")

  let shopName = ""
  if (user.role === "superadmin" || user.role === "supervisor") {
    shopName = selectedShop ? selectedShop.name : "All Shops"
  } else if (user.shop) {
    shopName = user.shop.name
  }

  return (
    <Box m="20px">
      <Box mb={4}>
        <Typography variant="h2" fontWeight="bold" color="text.primary">
          PRODUITS DE LA BOUTIQUE - {shopName}
        </Typography>
        <Typography variant="h5" color="text.secondary">
          Management des Produits
        </Typography>
      </Box>
      <Card elevation={3} sx={{ mb: 4, backgroundColor: "background.paper" }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                label="Recherche"
                variant="outlined"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            {(user.role === "superadmin" || user.role === "supervisor") && (
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Boutique</InputLabel>
                  <Select
                    value={selectedShop ? selectedShop._id : "all"}
                    onChange={handleShopChange}
                    label="Boutique"
                    IconComponent={ArrowDropDown}
                  >
                    <MenuItem value="all">Toutes les Boutiques</MenuItem>
                    {electronicShops.map((shop) => (
                      <MenuItem key={shop._id} value={shop._id}>
                        {shop.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            {user.role !== "supervisor" && (
              <Grid item xs={12} md={4}>
                <Button
                  onClick={handleAddOpen}
                  variant="contained"
                  fullWidth
                  startIcon={<AddCircleOutline />}
                  sx={{
                    backgroundColor: "primary.main",
                    "&:hover": {
                      backgroundColor: "primary.dark",
                    },
                  }}
                >
                  Ajouter le produit
                </Button>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Add Product Dialog */}
      <Dialog
        open={openAdd}
        onClose={handleAddClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: "12px",
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "primary.main",
            color: "white",
            py: 2,
          }}
        >
          <Typography variant="h5" component="div">
            Ajouter le produit
          </Typography>
        </DialogTitle>
        <ProductForm
          initialProduct={{
            name: "",
            category: "",
            price_achat: "",
            stock: "",
          }}
          isEdit={false}
          onSubmit={handleAddProduct}
          onCancel={handleAddClose}
        />
      </Dialog>

      {/* Edit Product Dialog */}
      <Dialog
        open={openEdit}
        onClose={handleEditClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: "12px",
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "primary.main",
            color: "white",
            py: 2,
          }}
        >
          <Typography variant="h5" component="div">
            Editer le produit
          </Typography>
        </DialogTitle>
        {selectedProduct && (
          <ProductForm
            initialProduct={selectedProduct}
            isEdit={true}
            onSubmit={handleEditProduct}
            onCancel={handleEditClose}
          />
        )}
      </Dialog>

      {/* Achat Product Dialog */}
      <Dialog
        open={openAchat}
        onClose={handleAchatClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: "12px",
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "primary.main",
            color: "white",
            py: 2,
          }}
        >
          <Typography variant="h5" component="div">
            Achat du produit - <strong>{selectedProduct?.name}</strong>
          </Typography>
        </DialogTitle>
        <AchatForm onSubmit={handleAchatSubmit} />
        <DialogContent>
          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
            Historique des achats
          </Typography>
          <Paper sx={{ maxHeight: 300, overflow: "auto", mt: 2, borderRadius: "8px" }}>
            <List>
              {achatProducts.map((achat) => (
                <ListItem
                  key={achat._id}
                  divider
                  sx={{
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography variant="subtitle1" fontWeight="bold">
                        Quantité: {achat.quantity}
                      </Typography>
                    }
                    secondary={
                      <>
                        <Typography component="span" variant="body2">
                          Date: {moment(achat.date).format("DD-MM-YYYY")}
                        </Typography>
                        <br />
                        <Typography component="span" variant="body2">
                          Utilisateur: {achat.user.name}
                        </Typography>
                        {achat.description && (
                          <>
                            <br />
                            <Typography component="span" variant="body2">
                              Description: {achat.description}
                            </Typography>
                          </>
                        )}
                      </>
                    }
                  />
                  {(user.role === "superadmin" || user.role === "supervisor") && (
                    <Box>
                      <Tooltip title="Modifier" arrow TransitionComponent={Zoom}>
                        <IconButton onClick={() => handleAchatEdit(achat._id)} sx={{ color: "primary.main" }}>
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Supprimer" arrow TransitionComponent={Zoom}>
                        <IconButton onClick={() => handleAchatDelete(achat._id)} sx={{ color: "error.main" }}>
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </ListItem>
              ))}
            </List>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleAchatClose} variant="outlined" color="primary">
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      <Box
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
            backgroundColor: "background.paper",
            borderRadius: 2,
            overflow: "hidden",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: `1px solid ${alpha("#000", 0.1)}`,
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "background.neutral",
            borderBottom: "none",
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: "background.neutral",
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: alpha("#000", 0.04),
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${colors.grey[100]} !important`,
          },
        }}
      >
        <DataGrid
          rows={filteredProducts}
          columns={columns}
          components={{
            Toolbar: () => <GridToolbar />,
            NoRowsOverlay: () => (
              <Stack height="100%" alignItems="center" justifyContent="center">
                <Inventory2Outlined sx={{ fontSize: 64, color: "text.secondary" }} />
                <Typography color="text.secondary">Aucun produit trouvé</Typography>
              </Stack>
            ),
          }}
          loading={loading}
          getRowId={(row) => row._id}
        />
      </Box>
    </Box>
  )
}

export default Produit
